import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  AddFormComponent,
  FormField,
} from '@shared/components/add-form/add-form.component';
import { CarrierFormConfig } from '../../carrier.config';
import { CarrierService } from '../../services/carrier.service';
import { LogisticsProviderAccountDTO } from '@app/api/after-sales-service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import {
  buildCarrierPayload,
  mapCarrierToFormData,
  parseCarrierIdParam,
  updateCarrierConnectionFieldVisibility,
  updateCarrierReadonlyFields,
} from '../../utils/carrier-form.util';

@Component({
  selector: 'app-carrier-form-wrapper',
  standalone: true,
  imports: [CommonModule, ToastModule, AddFormComponent, TranslateModule],
  providers: [MessageService],
  templateUrl: './carrier-form-wrapper.component.html',
  styleUrls: ['./carrier-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierFormWrapperComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly carrierService = inject(CarrierService);
  private readonly messageService = inject(MessageService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() config!: CarrierFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @ViewChild('addForm') addFormComponent?: AddFormComponent;

  // Data signals
  loading = signal(false);
  initialLoading = signal(true);
  formFields = signal<FormField[]>([]);
  formData = signal<any>({});
  carrierId = signal<number | null>(null);
  private skipCanDeactivate = false;

  ngOnInit(): void {
    this.initializeFormFields();
    this.loadCarrierData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormFields(): void {
    const fields = this.config.formFields.map((field) => ({ ...field }));
    updateCarrierConnectionFieldVisibility(fields, null, this.viewMode, this.editMode);
    updateCarrierReadonlyFields(fields, this.editMode);
    this.formFields.set(fields);
  }

  private loadCarrierData(): void {
    if (!this.editMode && !this.viewMode) {
      this.initialLoading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    const carrierId = parseCarrierIdParam(id);
    if (carrierId == null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    this.carrierId.set(carrierId);
    this.loading.set(true);

    this.carrierService
      .getCarrierById(carrierId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (carrier: LogisticsProviderAccountDTO) => {
          const formData = mapCarrierToFormData(carrier);
          this.formData.set(formData);
          const fields = [...this.formFields()];
          updateCarrierConnectionFieldVisibility(
            fields,
            carrier.providerCode,
            this.viewMode,
            this.editMode
          );
          updateCarrierReadonlyFields(fields, this.editMode);
          this.formFields.set(fields);
          this.loading.set(false);
          this.initialLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'carrier.messages.error.failed_to_load_carrier'
            ),
          });
          this.router.navigate([this.config.listRoute]);
        },
      });
  }

  onFormValueChanges(formData: any): void {
    const providerCode = formData.providerCode;
    const fields = [...this.formFields()];

    updateCarrierConnectionFieldVisibility(
      fields,
      providerCode,
      this.viewMode,
      this.editMode
    );

    this.formFields.set([...fields]);
    this.cdr.markForCheck();
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.router.navigate([this.config.listRoute]);
      return;
    }

    if (event.action === 'save') {
      this.handleSave(event.data);
      return;
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private handleSave(formData: any): void {
    if (!formData) {
      return;
    }

    const payload = buildCarrierPayload(formData);

    this.loading.set(true);

    if (this.editMode && this.carrierId()) {
      this.carrierService
        .updateCarrier(this.carrierId()!, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'carrier.messages.success.carrier_updated'
              ),
            });
            this.router.navigate([this.config.listRoute]);
          },
          error: (error) => {
            this.loading.set(false);
            const fallbackKey = 'carrier.messages.error.failed_to_update_carrier';
            this.errorDialogService.showError(this.errorTranslatorService.translate(error.error?.message, fallbackKey));
          },
        });
    } else {
      this.carrierService
        .createCarrier(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (createdCarrier: LogisticsProviderAccountDTO) => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'carrier.messages.success.carrier_created'
              ),
            });

            // After creating a new carrier, go to the new carrier detail page.
            if (createdCarrier?.id) {
              this.router.navigate(['/configuration/carrier/view', createdCarrier.id]);
              return;
            }

            // Fallback to list page if API response doesn't contain id.
            this.router.navigate([this.config.listRoute]);
          },
          error: (error) => {
            this.loading.set(false);
            const fallbackKey = 'carrier.messages.error.failed_to_create_carrier';
            this.errorDialogService.showError(this.errorTranslatorService.translate(error.error?.message, fallbackKey));
          },
        });
    }
  }

}
